import { useEffect, useRef, useState } from "react";
import { Loader2, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const dollars = (cents) => (cents / 100).toFixed(cents % 100 ? 2 : 0);
const errText = (e) => e?.response?.data?.detail || "That promo code didn't work";

// "Have a promo code?" — applies an admin-created code to the pending embedded
// checkout. Also auto-applies a code carried in from a ?code= link.
export const PromoCodeBox = ({ sessionId, coupon, onUpdate, onFreeUnlock }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const autoTried = useRef(false);

  const submit = async (value, { silent = false } = {}) => {
    const clean = (value || "").trim().toUpperCase();
    if (!clean) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/intent/${encodeURIComponent(sessionId)}/coupon`, { code: clean });
      localStorage.removeItem("lbj_promo");
      if (data.free_unlock) {
        toast.success("Promo covered the whole package — unlocking your jingle!");
        onFreeUnlock(data);
        return;
      }
      onUpdate(data);
      setCode("");
      toast.success(`Promo ${data.coupon.code} applied — ${data.coupon.description}`);
    } catch (e) {
      localStorage.removeItem("lbj_promo");
      if (!silent) toast.error(errText(e));
      else setOpen(true);
    }
    setBusy(false);
  };

  useEffect(() => {
    if (autoTried.current || coupon) return;
    autoTried.current = true;
    const fromUrl = new URLSearchParams(window.location.search).get("code");
    const stored = fromUrl || localStorage.getItem("lbj_promo");
    if (stored) {
      setCode(stored.toUpperCase());
      submit(stored, { silent: true });
    }
  }, [coupon]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/payments/intent/${encodeURIComponent(sessionId)}/coupon`);
      onUpdate(data);
      setOpen(false);
      toast("Promo removed");
    } catch (e) {
      toast.error(errText(e));
    }
    setBusy(false);
  };

  if (coupon) {
    return (
      <div data-testid="promo-applied" className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-400/30 bg-emerald-400/5 px-4 py-2.5 text-sm">
        <span className="flex items-center gap-2 text-emerald-300">
          <Tag className="h-4 w-4" /> <span className="font-mono font-bold">{coupon.code}</span>
          <span className="text-emerald-400/80">· {coupon.description}</span>
        </span>
        <button data-testid="promo-remove" type="button" onClick={remove} disabled={busy}
                className="text-slate-500 hover:text-white transition-colors cursor-pointer disabled:opacity-50" title="Remove promo">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button data-testid="promo-toggle" type="button" onClick={() => setOpen(true)}
              className="mt-4 text-sm text-slate-500 hover:text-gold transition-colors cursor-pointer flex items-center gap-1.5">
        <Tag className="h-3.5 w-3.5" /> Have a promo code?
      </button>
    );
  }

  return (
    <form data-testid="promo-form" onSubmit={(e) => { e.preventDefault(); submit(code); }} className="mt-4 flex gap-2">
      <input
        data-testid="promo-input"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="PROMO CODE"
        autoFocus
        className="flex-1 min-w-0 bg-[#101014] border border-white/10 rounded-xl px-4 py-2.5 text-sm font-mono tracking-wider outline-none focus:border-gold/60"
      />
      <button data-testid="promo-apply" type="submit" disabled={busy || !code.trim()}
              className="bg-white/10 hover:bg-white/15 text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
      </button>
    </form>
  );
};

export const PromoLine = ({ coupon }) => coupon ? (
  <div data-testid="checkout-promo-line" className="flex justify-between text-emerald-400">
    <span>Promo <span className="font-mono">{coupon.code}</span> ({coupon.description})</span>
    <span>−${dollars(coupon.discount_cents)}</span>
  </div>
) : null;
